import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { School, BarChart3, Users, BookOpen, Receipt, LogOut, MessageCircle, Volume2, MessageSquare } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { LuReceiptIndianRupee } from 'react-icons/lu';

interface SidebarItemProps {
  icon: React.ReactNode;
  text: string;
  to: string;
  active: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ icon, text, to, active }) => {
  return (
    <Link
      to={to}
      className={`flex items-center p-3 mb-2 rounded-lg transition-colors ${
        active 
          ? 'bg-pink-100 text-pink-700' 
          : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <div className="mr-3">{icon}</div>
      <span className="font-medium">{text}</span>
    </Link>
  );
};

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  // Define sidebar items based on user role
  const getNavItems = () => {
    const adminItems = [
      { icon: <BarChart3 size={20} />, text: 'Dashboard', to: '/dashboard' },
      { icon: <Users size={20} />, text: 'Students', to: '/students' },
      { icon: <BookOpen size={20} />, text: 'Attendance', to: '/attendance' },
      { icon: <BookOpen size={20} />, text: 'Attendance Summary', to: '/attendance-summary' },
      { icon: <LuReceiptIndianRupee size={20} />, text: 'Fees', to: '/fees' },
      { icon: <MessageSquare size={20} />, text: 'Class Announcments', to: '/class-announcements' },
      { icon: <Volume2 size={20} />, text: 'Announcments', to: '/announcements' },
      { icon: <MessageSquare size={20} />, text: 'Parent Chat', to: '/parent-chat' },
    ];
    
    const teacherItems = [
      { icon: <BarChart3 size={20} />, text: 'Dashboard', to: '/dashboard' },
      { icon: <Users size={20} />, text: 'Students', to: '/students' },
      { icon: <BookOpen size={20} />, text: 'Attendance', to: '/attendance' },
      { icon: <BookOpen size={20} />, text: 'Attendance Summary', to: '/attendance-summary' },
      { icon: <MessageSquare size={20} />, text: 'Class Announcments', to: '/class-announcements' },

    ];
    
    switch (user?.role) {
      case 'admin':
        return adminItems;
      case 'teacher':
        return teacherItems;
      default:
        return [];
    }
  };
  
  return (
    <div className="w-64 h-screen bg-white border-r border-gray-200 p-4 flex flex-col">
      <div className="mb-8 flex flex-col items-center">
        <img src="/assets/logo.png" alt="School Logo" className="h-24 w-auto rounded-full" />
        <h1 className="text-xl font-bold text-gray-800 mt-4">EuroWisKids</h1>
      </div>
      
      <div className="flex-1">
        {getNavItems().map((item, index) => (
          <SidebarItem
            key={index}
            icon={item.icon}
            text={item.text}
            to={item.to}
            active={isActive(item.to)}
          />
        ))}
      </div>
      
      <div className="mt-auto border-t border-gray-200 pt-4">
        <button 
          onClick={logout}
          className="flex items-center p-3 w-full rounded-lg text-gray-600 hover:bg-gray-100"
        >
          <LogOut size={20} className="mr-3" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;